import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { Button, Popover } from '@superops/ui-design-system';
import {
  DATE_PICKER_DATE_FORMAT,
  DATE_TIME_FORMAT,
  DATE_FORMAT,
  getTranslatedText
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { SVGIcon } from '@superops/super-icons';
import { range } from 'lodash';
import {
  DATE_OPERATORS,
  MONTH,
  MONTH_RANGE_OPERATORS,
  YEAR_RANGE_OPERATORS,
  LAST_THIRTY_DAYS
} from '../../constants';
import {
  DateFilterWrapper,
  DateItemWrapper,
  FilterOptionHolder,
  RangeSelector
} from './styles';

const DateFilter = (props) => {
  const {
    intl,
    setDate,
    date,
    isNewLayout = false,
    placeholderLabel,
    disabled = false,
    isShowDatePickerOnly = false,
    enableDatePickerOnlyRange = false,
    isShowMonthPickerOnly = false,
    isYearRangeOnly = false,
    enableDatePicker = true,
    isLastThirtyDays = false,
    minDateRange = null,
    maxDateRange = null,
    dateFormat = DATE_PICKER_DATE_FORMAT[DATE_FORMAT],
    showTimeInput = false,
    showDateItemSelector = true
  } = props;

  const [currentMode, setCurrentMode] = useState(
    typeof date === 'string' && date?.startsWith('placeholder') ? date : null
  );

  const [enableDateRange, setEnableDateRange] = useState(
    (date && typeof date === 'object') || enableDatePickerOnlyRange
  );

  const [startDate, setStartDate] = useState(
    date
      ? typeof date === 'string'
        ? date?.startsWith('placeholder')
          ? null
          : new Date(date)
        : new Date(date[0])
      : null
  );

  const [endDate, setEndDate] = useState(
    date && typeof date === 'object' ? new Date(date[1]) : null
  );

  const toggleEnableDateRange = useCallback(() => {
    if (enableDateRange) {
      // setStartDate(new Date());
      setEndDate(null);
    }
    setCurrentMode(null);
    setEnableDateRange(true);
  });
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopover = () => {
    setPopoverActive(!popoverActive);
  };

  const closePopOver = useCallback((start, end) => {
    if (enableDateRange) {
      if (start && end) {
        setPopoverActive(!popoverActive);
        setCurrentMode(null);
      }
    } else {
      setPopoverActive(!popoverActive);
    }
  });

  // const [currentSelection, setCurrentSelection] = useState(null);

  const formatDate = (dt, time, endTime = false) => {
    if (time) {
      if (endTime) {
        if (isShowMonthPickerOnly) {
          return dayjs(dt)
            .endOf('month')
            .endOf('day')
            .format(DATE_TIME_FORMAT);
        }
        return dayjs(dt)
          .endOf('day')
          .format(DATE_TIME_FORMAT);
      }
      return dayjs(dt).format(DATE_TIME_FORMAT);
    }
    return dayjs(dt).format(DATE_FORMAT);
  };

  useEffect(() => {
    if (enableDateRange && startDate && endDate) {
      setDate([
        formatDate(startDate, true, false),
        formatDate(endDate, true, true)
      ]);
    }
    if (!enableDateRange && !currentMode && startDate && !endDate) {
      setDate(formatDate(startDate, showTimeInput, false));
    }
  }, [startDate, endDate]);

  const activator = (
    <FilterOptionHolder
      onClick={!disabled ? togglePopover : undefined}
      isNewLayout={isNewLayout}
      disabled={disabled}
    >
      <span
        className={
          date ? 'fx actr filter-text isPresent' : 'fx actr filter-text'
        }
      >
        <div className="flex actr">
          <div className={`fx jctr ${isNewLayout ? 'ml-10' : 'mr-10'}`}>
            <SVGIcon name="DatePicker" color="#8b8b8b" />
          </div>
          <div className={isNewLayout ? 'ml-10 w150p ellipsis' : ''}>
            {date &&
              (typeof date === 'string' ? (
                showTimeInput ? (
                  <FormattedMessage id={formatDate(date, showTimeInput)} />
                ) : (
                  <FormattedMessage id={date} />
                )
              ) : (
                <FormattedMessage
                  id={`${formatDate(date[0])} to ${formatDate(date[1])}`}
                />
              ))}
            {!date && (
              <FormattedMessage
                id={
                  placeholderLabel || 'reports.report.filter.date.placeholder'
                }
              />
            )}
          </div>
        </div>
      </span>
      {isNewLayout ? (
        <div className="value_selector__indicator value_selector__dropdown-indicator css-tlfecz-indicatorContainer drpdwn">
          <div className="fx actr">
            <SVGIcon
              name="ArrowDown"
              size="lg"
              color="var(--sops-black-white-bg)"
            />
          </div>
        </div>
      ) : (
        <div className="value_selector__indicators css-1hb7zxy-IndicatorsContainer">
          <div className="triangle-down mr8" />
        </div>
      )}
    </FilterOptionHolder>
  );

  const handleOnChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    closePopOver(start, end);
  };

  const CustomHeader = ({
    date: pickerDate,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    decreaseYear,
    increaseYear
  }) => {
    const years = range(
      pickerDate.getFullYear() - 20,
      pickerDate.getFullYear() + 20,
      1
    );
    return (
      <div className="fx fx-sb  mbt14">
        <Button
          onClick={isShowMonthPickerOnly ? decreaseYear : decreaseMonth}
          disabled={prevMonthButtonDisabled}
          icon={<SVGIcon name="ArrowLeft" />}
          size="slim"
        />
        {!isShowMonthPickerOnly && (
          <RangeSelector
            value={MONTH[pickerDate.getMonth()]?.value}
            onChange={({ target: { value } }) =>
              changeMonth(MONTH.findIndex((el) => el.value === value))
            }
          >
            {MONTH.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {getTranslatedText(intl, opt.key)}
              </option>
            ))}
          </RangeSelector>
        )}
        <RangeSelector
          value={pickerDate.getFullYear()}
          onChange={({ target: { value } }) => changeYear(value)}
        >
          {years.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </RangeSelector>
        <Button
          onClick={isShowMonthPickerOnly ? increaseYear : increaseMonth}
          disabled={nextMonthButtonDisabled}
          icon={<SVGIcon name="ArrowRight" />}
          size="slim"
        />
      </div>
    );
  };

  const getOperators = () => {
    if (isShowMonthPickerOnly) {
      return MONTH_RANGE_OPERATORS;
    } else if (isYearRangeOnly) {
      return YEAR_RANGE_OPERATORS;
    } else if (isLastThirtyDays) {
      return LAST_THIRTY_DAYS;
    }
    return DATE_OPERATORS;
  };

  if (isShowDatePickerOnly) {
    const dataRange = minDateRange
      ? { minDate: minDateRange, maxDate: maxDateRange }
      : {};
    return (
      <DatePicker
        dateFormat={dateFormat}
        inline
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        {...dataRange}
        selectsRange
        onChange={handleOnChange}
        onMonthChange={() => {}}
        onYearChange={() => {}}
        renderCustomHeader={(prop) => <CustomHeader {...prop} />}
      />
    );
  }
  return (
    <Popover
      activator={activator}
      active={popoverActive}
      onClose={closePopOver}
      fullHeight
    >
      <DateFilterWrapper
        className={`flex sops-remove-shadow ${
          enableDatePicker && showDateItemSelector
            ? 'w480'
            : !showDateItemSelector
            ? 'w300' // reduce the width of popover after click date, if no need to select date item(yesterday,last month)
            : ''
        }`}
      >
        {showDateItemSelector && (
          <div
            className={`f11 ${
              enableDatePicker ? 'sops-calender-wrap-height' : ''
            } scroll`}
          >
            <DateItemSelector
              closePopOver={closePopOver}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setEnableDateRange={setEnableDateRange}
              setCurrentMode={setCurrentMode}
              setDate={setDate}
              formatDate={formatDate}
              operators={getOperators()}
              enableDateRange={enableDateRange}
              currentMode={currentMode}
              toggleEnableDateRange={toggleEnableDateRange}
              enableDatePicker={enableDatePicker}
            />
          </div>
        )}
        {enableDatePicker && (
          <div className="f11">
            {enableDateRange ? (
              <DatePicker
                dateFormat={dateFormat}
                inline
                selected={startDate}
                startDate={startDate}
                minDate={minDateRange}
                maxDate={maxDateRange}
                endDate={endDate}
                selectsRange
                showTimeInput={showTimeInput}
                showMonthYearPicker={isShowMonthPickerOnly}
                onChange={handleOnChange}
                onMonthChange={() => {}}
                onYearChange={() => {}}
                calendarClassName={
                  isShowMonthPickerOnly ? 'monthOnlyCalender full-width' : ''
                }
                renderCustomHeader={(prop) => <CustomHeader {...prop} />}
              />
            ) : (
              <DatePicker
                dateFormat={dateFormat}
                showTimeInput={showTimeInput}
                inline
                selected={startDate}
                minDate={minDateRange}
                maxDate={maxDateRange}
                onChange={(date) => {
                  if (isShowMonthPickerOnly) {
                    setEnableDateRange(true);
                    setStartDate(date);
                    setEndDate(date);
                    closePopOver();
                    setCurrentMode(null);
                  } else {
                    setStartDate(date);
                    // ✅ Close popover only if user clicked a day (not just changing time)
                    if (
                      showTimeInput &&
                      event?.target?.classList.contains('react-datepicker__day')
                    ) {
                      closePopOver();
                    }
                    if (!showTimeInput) {
                      closePopOver();
                    }
                    setCurrentMode(null);
                  }
                }}
                showMonthYearPicker={isShowMonthPickerOnly}
                onMonthChange={() => {}}
                onYearChange={() => {}}
                calendarClassName={
                  isShowMonthPickerOnly ? 'monthOnlyCalender full-width' : ''
                }
                renderCustomHeader={(prop) => <CustomHeader {...prop} />}
              />
            )}
          </div>
        )}
      </DateFilterWrapper>
    </Popover>
  );
};

const DateItemSelector = (props) => {
  const {
    closePopOver,
    setStartDate,
    setEndDate,
    setEnableDateRange,
    setCurrentMode,
    setDate,
    enableDateRange,
    currentMode,
    toggleEnableDateRange,
    operators,
    formatDate,
    enableDatePicker = true
  } = props;
  return (
    <>
      {operators.map((op) => (
        <DateItem
          data={op}
          closePopOver={closePopOver}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setEnableDateRange={setEnableDateRange}
          setCurrentMode={setCurrentMode}
          setDate={setDate}
          // setCurrentSelection={setCurrentSelection}
          isActive={currentMode === op.key}
          formatDate={formatDate}
        />
      ))}
      {enableDatePicker && (
        <DateItemWrapper
          onClick={toggleEnableDateRange}
          className={enableDateRange ? 'isActive' : ''}
        >
          <FormattedMessage id="placeholder.date.range" />
        </DateItemWrapper>
      )}
    </>
  );
};

const DateItem = (props) => {
  const {
    data,
    closePopOver,
    setEndDate,
    setStartDate,
    setEnableDateRange,
    setCurrentMode,
    setDate,
    isActive,
    formatDate
  } = props;
  const setDateToParent = () => {
    if (data?.key === 'lastYear') {
      const startDateLastYear = dayjs()
        .subtract(1, 'year')
        .startOf('year');
      const endDateLastYear = dayjs()
        .subtract(1, 'year')
        .endOf('year');
      setDate([
        formatDate(startDateLastYear, true, false),
        formatDate(endDateLastYear, true, true)
      ]);
    } else if (data?.key === 'thisYear') {
      const startDate = dayjs().startOf('year');
      const endDate = dayjs().endOf('year');
      setDate([
        formatDate(startDate, true, false),
        formatDate(endDate, true, true)
      ]);
    } else {
      setDate(data.key);
    }
  };
  return (
    <DateItemWrapper
      onClick={() => {
        setStartDate(null);
        setEndDate(null);
        setEnableDateRange(false);
        setDateToParent();
        setCurrentMode(data.key);
        closePopOver();
      }}
      className={isActive ? 'isActive' : ''}
    >
      <FormattedMessage id={data.label} />
    </DateItemWrapper>
  );
};

export default injectIntl(DateFilter);
